import {LocalStorage} from '@chekin/core';
import {useGuestGroup} from 'hooks/useGuestGroup';
import {isAdult} from 'utils/common';
import {useFetchParticipants} from 'hooks/auth/useFetchParticipants';
import {useGetReservationType} from 'hooks/useGetReservationType';
import {useReservation} from 'context/reservation/useReservation';
import {useSummary} from 'context/summary';
import {SHOWED_GUEST_GROUP_MODAL} from 'utils/constants';

function useComputedOnboardingDetails() {
  const {guestGroup, leaderGuest, isLoading: isLoadingGuestGroup} = useGuestGroup();
  const {data: participants, isInitialLoading: isLoadingParticipants} =
    useFetchParticipants();
  const {hasReservationGroup} = useGetReservationType();
  const {data: summary} = useSummary();
  const {allowEditingGuestNumber, reservationId} = useReservation();
  const areParticipantsCreated = guestGroup?.members?.some(guest => guest.participant_id);
  const areGuestCreated = Boolean(guestGroup?.members?.length);
  const knownNumberOfGuests = guestGroup?.known_number_of_guests ?? 0;
  const addedNumberOfGuests = guestGroup?.added_number_of_guests ?? 0;
  const remainingAllowedGuest = knownNumberOfGuests - addedNumberOfGuests;
  const isAnyParticipantAnAdult = participants?.some(participant =>
    isAdult(participant.birth_date),
  );
  const hasTaxes = summary?.taxes?.active;
  const localStorageKey = `${SHOWED_GUEST_GROUP_MODAL}-${reservationId}`;
  const isNumberOfGuestsStepShown = LocalStorage.get<{id: string; showed: boolean}>(
    localStorageKey,
  );

  const totalParticipants = participants?.length ?? 0;
  const areChildrenSameAsRemainingAllowedGuest =
    !isAnyParticipantAnAdult && remainingAllowedGuest === totalParticipants;

  const shouldAskListOfParticipants =
    !areChildrenSameAsRemainingAllowedGuest &&
    Boolean(participants?.length && remainingAllowedGuest > 0);

  const shouldAskLeaderGuest =
    !areChildrenSameAsRemainingAllowedGuest &&
    Boolean(participants?.length && !leaderGuest && !areParticipantsCreated);

  const shouldHideNumberOfGuests = !allowEditingGuestNumber && !hasTaxes;
  return {
    shouldAskReservationGroup: hasReservationGroup,
    shouldAskNumberOfGuests:
      !shouldHideNumberOfGuests && !isNumberOfGuestsStepShown?.showed,
    shouldAskListOfParticipants,
    shouldAskLeaderGuest,
    isLeaderRegistered: Boolean(leaderGuest),
    areGuestCreated,
    areChildrenSameAsRemainingAllowedGuest,
    remainingAllowedGuest,
    isLoadingDetails: isLoadingGuestGroup || isLoadingParticipants,
  };
}

export {useComputedOnboardingDetails};
